//
// Copyright (c) 2011-2019 by Curalate, Inc.
//
// Based off of the approach by @superandrew213 originally found
// here https://gist.github.com/superandrew213/22aca60ff7740eadc481b5d9a4eebdca
//

import { Component, ReactNode } from 'react';

interface IPositionsType {
  visibleColumnStopIndex: number;
  visibleRowStartIndex: number;
  visibleRowStopIndex: number;
  visibleStartIndex?: number;
  visibleStopIndex?: number;
}

interface IRawPositionsType {
  startIndex?: number;
  stopIndex?: number;
  rowStartIndex?: number;
  rowStopIndex?: number;
  columnStartIndex?: number;
  columnStopIndex?: number;
}

export interface IChangedVisibilityType {
  itemsChangedToHidden: IChangedVisibilityItemType[];
  itemsChangedToVisible: IChangedVisibilityItemType[];
}

export interface IChangedVisibilityItemType {
  index: number;
  item: any;
}

interface IChildrenProps {
  onItemsRendered: (positionsData: IRawPositionsType) => void;
}

interface IOwnProps {
  items: any[];
  numRowsFromBottom: number;
  children: (childrenProps: IChildrenProps) => ReactNode;
  onTopReached?: () => void;
  onEndReached?: () => void;
  onItemsVisibilityChange?: (data: IChangedVisibilityType) => void;
}

class VirtualPositionTracker extends Component<IOwnProps> {
  public _lastPositions: IPositionsType = null;

  public render() {
    return this.props.children({
      onItemsRendered: this._onItemsRendered,
    });
  }

  public _onItemsRendered = (positionsData: IRawPositionsType): void => {
    const { items, onTopReached, onEndReached, onItemsVisibilityChange, numRowsFromBottom } = this.props;
    const positions = this._getPositions(positionsData);
    const { visibleRowStartIndex, visibleRowStopIndex } = positions;
    const scrollDirection = this._getScrollDirection(positions);
    const columnCount = this._getColumnCount(positions);
    const lastRowIndex = Math.ceil(items.length / columnCount) - 1;

    // Bottom already visible without scroll
    if (
      scrollDirection === undefined &&
      visibleRowStopIndex === lastRowIndex
    ) {
      // Because we potentially immediately load additional content as soon as
      // we render a grid, use a setTimeout here to avoid conflicting with an
      // angular digest cycle.
      setTimeout(onEndReached, 0);
    }

    // Reached top
    if (
      onTopReached &&
      visibleRowStartIndex === 0 &&
      scrollDirection === 'BACKWARD' &&
      this._didVisibleRowStartIndexChange(positions)
    ) {
      onTopReached();
    }

    // Reached bottom
    if (
      onEndReached &&
      visibleRowStopIndex >= (lastRowIndex - numRowsFromBottom) &&
      scrollDirection === 'FORWARD' &&
      this._didVisibleRowStopIndexChange(positions)
    ) {
      onEndReached();
    }

    // Handle items that changed visibility
    if (onItemsVisibilityChange) {
      const itemsThatChangedVisibility = this._getItemsThatChangedVisibility(positions);
      if (itemsThatChangedVisibility) {
        onItemsVisibilityChange(itemsThatChangedVisibility);
      }
    }

    // Store last positions
    this._lastPositions = positions;
  }

  // The code below adds support for both the List and Grid components from the react-virtualized library
  public _getPositions = (positionsData: IRawPositionsType): IPositionsType => ({
    visibleColumnStopIndex: positionsData.columnStopIndex,
    visibleRowStartIndex: positionsData.startIndex != null
      ? (positionsData.startIndex)
      : (positionsData.rowStartIndex),
    visibleRowStopIndex: positionsData.stopIndex != null
      ? (positionsData.stopIndex)
      : (positionsData.rowStopIndex),
  })

  public _getColumnCount = (positions: IPositionsType): number => {
    return positions.visibleColumnStopIndex ? positions.visibleColumnStopIndex + 1 : 1;
  }

  public _getScrollDirection = (positions: IPositionsType): 'BACKWARD' | 'FORWARD' => {
    const {
      visibleRowStartIndex,
      visibleRowStopIndex,
    } = positions;

    if (this._lastPositions == null) {
      return;
    }

    if (
      this._lastPositions.visibleRowStartIndex > visibleRowStartIndex ||
      this._lastPositions.visibleRowStopIndex > visibleRowStopIndex
    ) {
      return 'BACKWARD';
    }

    if (
      this._lastPositions.visibleRowStartIndex < visibleRowStartIndex ||
      this._lastPositions.visibleRowStopIndex < visibleRowStopIndex
    ) {
      return 'FORWARD';
    }
  }

  public _getItemsThatChangedVisibility = (positions: IPositionsType): IChangedVisibilityType | void => {
    const { items } = this.props;
    const {
      visibleRowStartIndex,
      visibleRowStopIndex,
    } = positions;
    const scrollDirection = this._getScrollDirection(positions);

    // Ensure scroll has started
    if (!scrollDirection) {
      return;
    }

    const columnCount = this._getColumnCount(positions);
    const itemsChangedToHidden = [];
    const itemsChangedToVisible = [];
    const visibleRowStartIndexChanged = this._didVisibleRowStartIndexChange(positions);
    const visibleRowStopIndexChanged = this._didVisibleRowStopIndexChange(positions);

    // Hidden
    const hiddenItemsStartIndex = scrollDirection === 'FORWARD'
      ? (visibleRowStartIndexChanged ? (visibleRowStartIndex - 1) * columnCount : null)
      : (visibleRowStopIndexChanged ? (visibleRowStopIndex + 1) * columnCount : null);

    // Visible
    const visibleItemsStartIndex = scrollDirection === 'FORWARD'
      ? (visibleRowStopIndexChanged ? visibleRowStopIndex * columnCount : null)
      : (visibleRowStartIndexChanged ? visibleRowStartIndex * columnCount : null);

    for (let i = 0; i < columnCount; i += 1) {
      if (hiddenItemsStartIndex != null) {
        itemsChangedToHidden.push({
          index: hiddenItemsStartIndex + i,
          item: items[hiddenItemsStartIndex + i],
        });
      }
      if (visibleItemsStartIndex != null) {
        itemsChangedToVisible.push({
          index: visibleItemsStartIndex + i,
          item: items[visibleItemsStartIndex + i],
        });
      }
    }

    return {
      itemsChangedToHidden,
      itemsChangedToVisible,
    };
  }

  public _didVisibleRowStartIndexChange = (positions: IPositionsType): boolean => {
    return (
      this._lastPositions != null &&
      this._lastPositions.visibleRowStartIndex !== positions.visibleRowStartIndex
    );
  }

  public _didVisibleRowStopIndexChange = (positions: IPositionsType): boolean => {
    return (
      this._lastPositions != null &&
      this._lastPositions.visibleRowStopIndex !== positions.visibleRowStopIndex
    );
  }
}

export default VirtualPositionTracker;
